import { useEffect } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  adicionarMeses,
  diasNoMes,
  mesDe,
  ROTULO_CENTRO_DE_CUSTO,
  ROTULO_FORMA_PAGAMENTO,
  type PaginaDoExtrato,
  type ResumoFinanceiro,
} from '@arena/shared/schemas';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { CardDeHorasACobrar } from '../agenda/HorasACobrar.jsx';
import { ErroDeApi, ErroDeRede } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Barras } from '../graficos/Barras.jsx';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { mesPorExtenso, nomeDoMes } from '../planos/formatos.js';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Card, Kpi, Status, Tabela, Tag, Vazio, type Tendencia } from '../ui/primitivos.jsx';
import { BarraDeFiltros } from './BarraDeFiltros.jsx';
import {
  consultaDoExtrato,
  corDoLancamento,
  definirMes,
  lerFiltrosDoExtrato,
  lerLancamentoDaUrl,
  lerMesDaUrl,
  situacaoDoLancamento,
  temFiltroNoExtrato,
  valorDoLancamento,
  type FiltrosDoExtrato,
} from './formatos.js';
import { ModalDeLancamento } from './ModalDeLancamento.jsx';
import { ModalDoLancamento } from './ModalDoLancamento.jsx';
import { NavegacaoDoMes } from './NavegacaoDoMes.jsx';
import { CardDeResultadoPorCentro } from './ResultadoPorCentro.jsx';

/**
 * `#v-fin` — o razão: caixa, entradas e saídas por semana, saídas por
 * categoria, extrato de últimos lançamentos.
 *
 * A "Projeção de caixa · 90 dias" do protótipo fica de fora — os
 * documentos 16 e 17 entregam, porque dependem de dados que este
 * módulo ainda não tem (ver Arena_backend/Docs/plano/11-financeiro-
 * razao.md, seção Escopo).
 *
 * TODOS os números vêm de `resumo`, já calculados no servidor SOBRE AS
 * LINHAS QUE O PERFIL ALCANÇA — para a Gestão, sem a folha em nenhum
 * total. A tela só formata; nunca soma de novo.
 *
 * O mês e os filtros do extrato moram na URL (`?mes=2026-09&tipo=Saida`),
 * como em `#v-planos` e na agenda: o link colado abre o mesmo recorte, e o
 * F5 não o perde. O extrato é paginado por cursor, com "Carregar mais".
 *
 * Cada linha do extrato abre o detalhe do lançamento (`?lancamento=<id>`),
 * onde estão as ações — realizar, editar, estornar, cancelar.
 *
 * Ver: Arena_backend/Docs/plano/11-financeiro-razao.md
 */

const CORES_DAS_CATEGORIAS = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
];

export function PaginaDoFinanceiro() {
  const [params, setParams] = useSearchParams();
  const { pode } = useSessao();

  // Mês e filtros chegam da URL já validados: o que o servidor recusaria
  // vira "sem filtro" aqui, e não um 400 na cara de quem colou o link.
  const mesPedido = lerMesDaUrl(params);
  const filtros = lerFiltrosDoExtrato(params);

  function mudar(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }

  // Ao trocar de mês ou de filtro, o que estava na tela fica até o novo
  // chegar — em vez de piscar um esqueleto a cada clique na seta. O resumo
  // não depende dos filtros do extrato (só do mês), e por isso a chave dele
  // não os leva: filtrar a lista não refaz os números.
  const resumoQuery = useQuery({
    queryKey: ['financeiro', 'resumo', mesPedido ?? 'atual'],
    queryFn: () => financeiroApi.resumo(mesPedido),
    placeholderData: keepPreviousData,
  });
  const extratoQuery = useInfiniteQuery({
    queryKey: ['financeiro', 'extrato', mesPedido ?? 'atual', filtros],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      financeiroApi.extrato(consultaDoExtrato(mesPedido, filtros, pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  // A invalidação de ['financeiro'] ao criar acontece DENTRO do modal
  // (mesmo padrão de `ModalDeReserva.tsx`): quem sabe que teve sucesso
  // é quem chama a mutação.

  // `?mes=` com um mês que não existe, ou com o mês corrente (que é o
  // padrão e não precisa estar na URL), sai sozinho.
  const hoje = resumoQuery.data?.hoje;
  useEffect(() => {
    const pedido = params.get('mes');
    if (pedido === null) return;
    if (mesPedido === undefined || (hoje !== undefined && pedido === mesDe(hoje))) {
      const novos = new URLSearchParams(params);
      novos.delete('mes');
      setParams(novos, { replace: true });
    }
  }, [params, setParams, mesPedido, hoje]);

  const podeCriar = pode('financeiro', 'criar');
  const novo = params.get('novo') === '1';
  // O detalhe abre por `?lancamento=<id>` — só um id de verdade; o resto da
  // URL é ignorado, como no mês e nos filtros.
  const lancamentoAberto = lerLancamentoDaUrl(params);

  const abrirNovo = () => mudar((p) => p.set('novo', '1'));
  const fecharNovo = () => mudar((p) => p.delete('novo'));
  const abrirLancamento = (id: string) => mudar((p) => p.set('lancamento', id));
  const fecharLancamento = () => mudar((p) => p.delete('lancamento'));

  if (resumoQuery.isPending) {
    return (
      <>
        <CabecalhoDoFinanceiro titulo="Carregando o financeiro…" />
        <div className="grid g4">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={2} />
          ))}
        </div>
        <EsqueletoDeCard linhas={8} />
      </>
    );
  }

  if (resumoQuery.isError) {
    const erro = resumoQuery.error;
    return (
      <>
        <CabecalhoDoFinanceiro
          titulo="Não consegui carregar o financeiro"
          // Um mês que não carrega não pode prender a pessoa nele: sem o
          // parâmetro, a tela volta ao mês corrente.
          acoes={
            mesPedido && (
              <Botao variante="contorno" pequeno onClick={() => mudar((p) => p.delete('mes'))}>
                Este mês
              </Botao>
            )
          }
        />
        <Erro
          tipo={tipoDoErro(erro)}
          codigo={erro instanceof ErroDeApi ? erro.requestId : undefined}
          onTentarDeNovo={() => void resumoQuery.refetch()}
        />
      </>
    );
  }

  const resumo = resumoQuery.data;
  const mesCorrente = mesDe(resumo.hoje);
  // O mês PEDIDO, não o da resposta na tela: com `keepPreviousData` os dois
  // diferem enquanto o novo carrega, e as setas andam a partir do pedido.
  const mesExibido = mesPedido ?? mesCorrente;
  const irParaMes = (mes: string) => mudar((p) => definirMes(p, mes, mesCorrente));

  return (
    <>
      <CabecalhoDoFinanceiro
        titulo="Financeiro"
        subtitulo="Fluxo de caixa, entradas e saídas por centro de custo — e as horas de quadra que faltam cobrar."
        acoes={
          <>
            <NavegacaoDoMes mes={mesExibido} mesAtual={mesCorrente} onIrPara={irParaMes} />
            {podeCriar && (
              <Botao icone="plus" onClick={abrirNovo}>
                Novo lançamento
              </Botao>
            )}
          </>
        }
      />

      <Indicadores resumo={resumo} />
      <GraficosDoMes resumo={resumo} />
      {/* O que cada centro de custo rendeu no mês — só o realizado, pelo
          escopo do perfil. Vem do resumo: a tela não soma. */}
      <CardDeResultadoPorCentro resumo={resumo} />
      {/* 06 + 11 — o que a agenda registrou e o caixa ainda não recebeu.
          Fica ACIMA do extrato de propósito: o extrato é o que já
          aconteceu, e isto é o que falta fazer acontecer. */}
      <CardDeHorasACobrar
        titulo="Horas de quadra a cobrar"
        de={`${resumo.mes}-01`}
        ate={`${resumo.mes}-${String(diasNoMes(resumo.mes)).padStart(2, '0')}`}
        dica={`${nomeDoMes(resumo.mes)} · para fechar o aluguel`}
        vazio="Nenhuma hora de quadra em aberto no mês."
        limite={8}
      />
      <CardDeExtrato
        query={extratoQuery}
        mes={mesExibido}
        filtros={filtros}
        mudar={mudar}
        onAbrir={abrirLancamento}
      />

      {novo && podeCriar && <ModalDeLancamento hoje={resumo.hoje} onFechar={fecharNovo} />}
      {/* Um modal por vez: se a URL pede os dois, o de "Novo lançamento" vence. */}
      {lancamentoAberto && !(novo && podeCriar) && (
        <ModalDoLancamento
          id={lancamentoAberto}
          hoje={resumo.hoje}
          fuso={resumo.fuso}
          onFechar={fecharLancamento}
          onAbrir={abrirLancamento}
        />
      )}
    </>
  );
}

function CabecalhoDoFinanceiro({
  titulo,
  subtitulo,
  acoes,
}: {
  titulo: string;
  subtitulo?: string;
  acoes?: React.ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{titulo}</h1>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      {acoes && <div className="head-actions">{acoes}</div>}
    </div>
  );
}

/**
 * Os quatro KPIs do topo. A tendência (alta/baixa/estável) segue o
 * SINAL aritmético puro da variação — nunca um julgamento de "menos
 * saída é bom" (documento 11: "não tente decidir se... siga o sinal").
 */
function Indicadores({ resumo }: { resumo: ResumoFinanceiro }) {
  const variacaoCaixa = resumo.caixa.variacaoSemana;
  const entradasPct = variacaoPercentual(resumo.mesAtual.entradas, resumo.mesAnterior.entradas);
  const saidasPct = variacaoPercentual(resumo.mesAtual.saidas, resumo.mesAnterior.saidas);
  const margem =
    resumo.mesAtual.entradas > 0
      ? (resumo.mesAtual.geracao / resumo.mesAtual.entradas) * 100
      : null;
  const mesAnteriorTexto = nomeDoMes(adicionarMeses(resumo.mes, -1));
  // Mês depois do corrente: ainda não há realizado, e "100% a menos que o
  // mês passado" seria verdade aritmética e mentira prática. O que existe é
  // a previsão, e é ela que o cartão mostra.
  const aFrente = resumo.mes > mesDe(resumo.hoje);

  return (
    <div className="grid g4">
      <Kpi
        valor={dinheiro(resumo.caixa.saldo)}
        rotulo="Saldo em caixa hoje"
        tendencia={tendenciaDoSinal(variacaoCaixa)}
        variacao={
          variacaoCaixa === 0
            ? 'sem variação na semana'
            : `${variacaoCaixa > 0 ? '+' : '−'}${dinheiro(Math.abs(variacaoCaixa))} na semana`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.entradas)}
        rotulo="Entradas do mês"
        tendencia={aFrente ? 'estavel' : tendenciaDoSinal(entradasPct ?? 0)}
        variacao={
          aFrente
            ? `previsto ${dinheiro(resumo.mesAtual.entradasPrevistas)}`
            : entradasPct === null
              ? 'sem mês anterior para comparar'
              : `${formatarPercentual(entradasPct)} vs. ${mesAnteriorTexto}`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.saidas)}
        rotulo="Saídas do mês"
        tendencia={aFrente ? 'estavel' : tendenciaDoSinal(saidasPct ?? 0)}
        variacao={
          aFrente
            ? `previsto ${dinheiro(resumo.mesAtual.saidasPrevistas)}`
            : saidasPct === null
              ? 'sem mês anterior para comparar'
              : `${formatarPercentual(saidasPct)} vs. ${mesAnteriorTexto}`
        }
      />
      <Kpi
        valor={dinheiro(resumo.mesAtual.geracao)}
        rotulo="Geração de caixa"
        tendencia={tendenciaDoSinal(resumo.mesAtual.geracao)}
        variacao={
          // Na margem o sinal é a informação — "margem de 179%" num mês
          // que queimou caixa lê como lucro. Nas variações a seta já diz
          // a direção, e por isso `formatarPercentual` tira o sinal.
          margem === null
            ? 'sem entradas no mês'
            : `margem de ${margem < 0 ? '−' : ''}${formatarPercentual(margem)}`
        }
      />
    </div>
  );
}

/** "Entradas e saídas por semana" e "Saídas por categoria", lado a lado. */
function GraficosDoMes({ resumo }: { resumo: ResumoFinanceiro }) {
  const totalSaidas = resumo.mesAtual.saidas;

  return (
    <div className="grid g-2-1">
      <Card titulo="Entradas e saídas por semana" dica={nomeDoMes(resumo.mes)}>
        <Barras
          cores={['var(--serie-1)', 'var(--serie-3)']}
          legenda={[
            { rotulo: 'Entradas', cor: 'var(--serie-1)' },
            { rotulo: 'Saídas', cor: 'var(--serie-3)' },
          ]}
          colunas={resumo.semanas.map((semana) => ({
            x: semana.rotulo,
            valores: [semana.entradas, semana.saidas],
            dicas: [
              `Entradas de ${dinheiro(semana.entradas)}`,
              `Saídas de ${dinheiro(semana.saidas)}`,
            ],
          }))}
        />
      </Card>
      <Card titulo="Saídas por categoria" dica={nomeDoMes(resumo.mes)}>
        {resumo.saidasPorCategoria.length === 0 ? (
          <Vazio>Nenhuma saída neste mês.</Vazio>
        ) : (
          <BarrasHorizontais
            itens={resumo.saidasPorCategoria.map((item, indice) => ({
              nome: item.categoria,
              valor: item.total,
              rotulo: dinheiro(item.total),
              cor: CORES_DAS_CATEGORIAS[indice % CORES_DAS_CATEGORIAS.length] as string,
              dica:
                totalSaidas > 0
                  ? `${formatarPercentual((item.total / totalSaidas) * 100)} das saídas.`
                  : dinheiro(item.total),
            }))}
          />
        )}
      </Card>
    </div>
  );
}

/**
 * "Últimos lançamentos" — o extrato do mês, com os filtros e o "Carregar
 * mais". Cada linha abre o detalhe do lançamento, e é lá que estão as
 * ações: a tabela continua sendo só leitura.
 *
 * A barra de filtros fica à vista em todo estado — carregando, vazio ou
 * com erro —, porque é ela que tira a pessoa de um recorte sem resultado.
 */
function CardDeExtrato({
  query,
  mes,
  filtros,
  mudar,
  onAbrir,
}: {
  query: UseInfiniteQueryResult<InfiniteData<PaginaDoExtrato>>;
  mes: string;
  filtros: FiltrosDoExtrato;
  mudar: (mudanca: (p: URLSearchParams) => void) => void;
  onAbrir: (lancamentoId: string) => void;
}) {
  const itens = query.data?.pages.flatMap((pagina) => pagina.itens) ?? [];
  // Mês ou filtro novo ainda carregando: a lista antiga fica na tela, mais
  // clara, e não se pede a próxima página dela.
  const trocando = query.isPlaceholderData;

  return (
    <Card titulo="Últimos lançamentos" dica={mesPorExtenso(mes)}>
      <BarraDeFiltros filtros={filtros} mudar={mudar} />

      {query.isPending && <EsqueletoDeTabela colunas={7} />}

      {query.isError && (
        <Erro
          tipo={tipoDoErro(query.error)}
          codigo={query.error instanceof ErroDeApi ? query.error.requestId : undefined}
          onTentarDeNovo={() => void query.refetch()}
        />
      )}

      {query.data && itens.length === 0 && (
        <Vazio>
          {temFiltroNoExtrato(filtros)
            ? 'Nenhum lançamento neste filtro.'
            : 'Nenhum lançamento neste mês.'}
        </Vazio>
      )}

      {itens.length > 0 && (
        <>
          <div aria-busy={trocando} style={trocando ? { opacity: 0.55 } : undefined}>
            <Tabela
              colunas={[
                'Data',
                'Descrição',
                'Categoria',
                'Centro de custo',
                'Forma',
                'Situação',
                'Valor',
              ]}
            >
              {itens.map((item) => {
                const situacao = situacaoDoLancamento(item);
                return (
                  // Como as faturas em `planos/TabelaDeFaturas`: a linha inteira
                  // abre o detalhe — e, por teclado, Enter ou Espaço.
                  <tr
                    key={item.id}
                    className="clickable"
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => onAbrir(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAbrir(item.id);
                      }
                    }}
                  >
                    <td>{dataCurta(item.data)}</td>
                    <td>{item.descricao}</td>
                    <td>{item.categoria}</td>
                    <td>
                      <Tag>{ROTULO_CENTRO_DE_CUSTO[item.centroDeCusto]}</Tag>
                    </td>
                    <td>
                      {item.formaPagamento ? ROTULO_FORMA_PAGAMENTO[item.formaPagamento] : '—'}
                    </td>
                    <td>
                      <Status situacao={situacao.situacao}>{situacao.rotulo}</Status>
                    </td>
                    {/* O sinal e a cor são do EFEITO no caixa: o estorno repete o
                        tipo do original com o valor negativo, e é o contrário dele. */}
                    <td className="num" style={{ color: corDoLancamento(item) }}>
                      {valorDoLancamento(item)}
                    </td>
                  </tr>
                );
              })}
            </Tabela>
          </div>
          {query.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage || trocando}
              >
                {query.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------
// Formatação — puro, sem estado
// ---------------------------------------------------------------------

function tendenciaDoSinal(valor: number): Tendencia {
  if (valor > 0) return 'alta';
  if (valor < 0) return 'baixa';
  return 'estavel';
}

/** `null` quando o mês anterior é zero — não dá para dizer "cresceu X%" de uma base zero. */
function variacaoPercentual(atual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return ((atual - anterior) / anterior) * 100;
}

function formatarPercentual(valor: number): string {
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(valor))}%`;
}

function tipoDoErro(erro: unknown): 'rede' | 'permissao' | 'falha' {
  if (erro instanceof ErroDeRede) return 'rede';
  if (erro instanceof ErroDeApi && erro.ehPermissao) return 'permissao';
  return 'falha';
}
